import { Component, OnInit } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { TherapistService } from '../../services/api/TherapistService';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Grid_TherapistModel } from '../../models/api/Grid_TherapistModel';
import { BookingService } from '../../services/api/BookingService';
import { BookingModel } from '../../models/api/BookingModel';
import { TherapistEventService } from '../../services/api/TherapistEventService';
import { Grid_Therapist_ExtModel } from '../../models/api/Grid_Therapist_ExtModel';
import { DatePipe } from '@angular/common';
import { ActiveTherapistModel } from '../../models/api/ActiveTherapistModel';
import { HelpersModule } from '../../helpers/helpers';
import { Observable, Subject, catchError, exhaustMap, from, map, of, takeUntil, tap, timer } from 'rxjs';

@Component({
  selector: 'app-therapist-list',
  standalone: true,
  imports: [MatCardModule, MatButtonModule, MatIconModule, MatProgressBarModule, DatePipe],
  providers: [TherapistService],
  templateUrl: './therapist-list.component.html',
  styleUrl: './therapist-list.component.scss'
})
export class TherapistListComponent implements OnInit {
  therapists: ActiveTherapistModel[] = [];
  eventOpen: boolean = false;
  isLoaded: boolean = false;
  private bookingGuid = localStorage.getItem('bookingGuid');
  bookingDetail = new BookingModel();

  public eventID: number;
  public eventName: string;
  public eventStartDate: Date;
  public eventEndDate: Date;

  private destroy$ = new Subject<void>();

  constructor(private router: Router, private therapistService: TherapistService, private therapistEventService: TherapistEventService, private bookingService: BookingService) {
    const navigation = this.router.getCurrentNavigation();
    this.eventID = navigation?.extras?.state?.['eventID'];
    this.eventName = navigation?.extras.state?.['eventName'];
    this.eventStartDate = navigation?.extras?.state?.['eventStartDate'];
    this.eventEndDate = navigation?.extras?.state?.['eventEndDate'];
  }

  ngOnInit(): void {

    if (!this.eventID || this.eventID === 0) {
      console.log("eventlist nav to client portal");
      this.router.navigate(['/client-portal/event-list']);
    }

    if (this.bookingGuid) {
      this.bookingService.getBooking(this.bookingGuid).subscribe(data => {
        this.bookingDetail = data;

        if (this.bookingDetail && (this.bookingDetail.bookingStatusID === 2 || this.bookingDetail.bookingStatusID === 3 || this.bookingDetail.bookingStatusID === 4 || this.bookingDetail.bookingStatusID === 5)) {
          this.router.navigate(['client-portal', 'queue', this.bookingGuid]);
          return;
        } else {
          localStorage.removeItem('bookingGuid');
        }
      })
    }

    //
    // if eventID is undefined this fires a 400 but doesn't cause issue, so assume we can just ignore and this will
    // get hit again with the correct data
    //
    if (this.eventID != undefined) {
      this.therapistService.getActiveByEvent(this.eventID).subscribe({
        next: (data) => {
          this.therapists = data;

          this.therapists.sort((a, b) => {
            const mapStatus = (t: any) => {
              const s = Number(t.therapistStatusId);
              if ([3, 4].includes(s) && t.acceptBookings) return 1; // Group 1
              return 2; // Group 2 (statuses 1/2 or 3/4 with no bookings)
            };

            const statusA = mapStatus(a);
            const statusB = mapStatus(b);

            // Sort by group (Group 1 first)
            const statusDiff = statusA - statusB;
            if (statusDiff !== 0) return statusDiff;

            if (statusA === 2) {
              // Group 2: startTime first
              const aHasStart = Boolean(a.startTime);
              const bHasStart = Boolean(b.startTime);
              if (aHasStart !== bHasStart) return aHasStart ? -1 : 1;
            }

            // Alphabetical fallback for both groups
            return a.firstName!.localeCompare(b.firstName!, 'en', { sensitivity: 'base' });
          });

          if (this.therapists.length > 0) {

            this.therapists.forEach((x, index) => {
              if (x.therapistId !== 1) {
                this.eventOpen = true;
              }
            });
          } else {
            this.eventOpen = false;
          }
          this.isLoaded = true;

          this.setupTimer();

        },
        error: (err) => {
        }
      })
    }
  }

  selectTherapist(therapist?: ActiveTherapistModel) {
    let selectedTherapistId: number = 0;

    if (therapist && therapist.therapistId) {
      selectedTherapistId = therapist.therapistId;
    }

    let therapistName: string = "";

    if (therapist) {
      therapistName = this.getDisplayName(therapist);
    } else {
      ''
    }


    this.router.navigate(['/client-portal/book'], { state: { therapistId: selectedTherapistId, therapistName, eventID: this.eventID, eventStartDate: this.eventStartDate, eventEndDate: this.eventEndDate, eventName: this.eventName } });
  }

  therapistStatusTypeLookup: { [key: number]: string } = {
    1: 'Offline',
    2: 'Clocked In',
    3: 'Available',
    4: 'On Break'
  };

  getDisplayName(therapist: Grid_Therapist_ExtModel): string {
    const sameFirstNameCount = this.therapists.filter(
      x => x.firstName === therapist.firstName
    ).length;

    return sameFirstNameCount > 1
      ? `${therapist.firstName} ${therapist.lastName!.charAt(0)}`
      : therapist.firstName!;
  }

  getThumbURL(url: string | undefined): string | undefined {
    if (url == undefined)
      return url;

    return HelpersModule.cloudinaryURLForceWidth(url, 120);
  }

  setupTimer(): void {
    timer(0, 5000)
      .pipe(
        exhaustMap(() => from(this.refreshTherapists())),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  refreshTherapists(): Observable<boolean> {
    //console.log("Do refresh therapists");

    return this.therapistService.getActiveByEvent(this.eventID)
      .pipe(
        map(data => {
          let therapists = data;

          therapists.sort((a, b) => {
            const mapStatus = (t: any) => {
              const s = Number(t.therapistStatusId);
              if ([3, 4].includes(s) && t.acceptBookings) return 1; // Group 1
              return 2; // Group 2 (statuses 1/2 or 3/4 with no bookings)
            };

            const statusA = mapStatus(a);
            const statusB = mapStatus(b);

            // Sort by group (Group 1 first)
            const statusDiff = statusA - statusB;
            if (statusDiff !== 0) return statusDiff;

            if (statusA === 2) {
              // Group 2: startTime first
              const aHasStart = Boolean(a.startTime);
              const bHasStart = Boolean(b.startTime);
              if (aHasStart !== bHasStart) return aHasStart ? -1 : 1;
            }

            // Alphabetical fallback for both groups
            return a.firstName!.localeCompare(b.firstName!, 'en', { sensitivity: 'base' });
          });

          if (therapists.length > 0) {

            therapists.forEach((x, index) => {
              if (x.therapistId !== 1) {
                this.eventOpen = true;
              }
            });
          } else {
            this.eventOpen = false;
          }

          //console.log("Therapists refreshed", therapists);

          this.therapists = therapists;
          return true;
        }),
        catchError(err => {
          console.error(err);

          return of(false);
        })
      );

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}


